import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../firebaseConfig';
import { getApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
    collection, addDoc, getDocs, query, where, deleteDoc, doc, serverTimestamp, orderBy, updateDoc 
} from 'firebase/firestore';
import { 
    FaPlus, FaTrash, FaUpload, FaCalendarTimes, FaDownload, FaSearch, 
    FaPaperclip, FaEdit, FaFilePdf, FaFileImage, FaFileAlt, FaEye
} from 'react-icons/fa';
import { IoTimeOutline } from "react-icons/io5";
import * as XLSX from 'xlsx';
import '../styles/AbsenteismoManager.css'; // CSS Novo

const STORAGE_PATH = 'atestados';
const TYPES = [
    { value: 'falta_injustificada', label: 'Falta Injustificada', color: '#e74c3c' },
    { value: 'atestado', label: 'Atestado Médico', color: '#2ecc71' },
    { value: 'atraso', label: 'Atraso', color: '#f39c12' },
    { value: 'saida_antecipada', label: 'Saída Antecipada', color: '#e67e22' },
    { value: 'suspensao', label: 'Suspensão', color: '#34495e' },
    { value: 'licenca', label: 'Licença', color: '#9b59b6' }
];

const AbsenteismoManager = ({ userData }) => {
    // --- ESTADOS ---
    const [absences, setAbsences] = useState([]);
    const [usersList, setUsersList] = useState([]); // Agora inclui photoURL
    const [loading, setLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Form
    const [selectedUser, setSelectedUser] = useState(null); 
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showUserSuggestions, setShowUserSuggestions] = useState(false);
    const [type, setType] = useState('atestado');
    const [date, setDate] = useState('');
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(0);
    const [reason, setReason] = useState('');
    const [observation, setObservation] = useState('');
    const [file, setFile] = useState(null);
    const [existingFileUrl, setExistingFileUrl] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const searchRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- FETCH DATA ---
    const fetchData = useCallback(async () => {
        if (!userData?.empresa_id) return;
        setLoading(true);
        try {
            const empresaId = userData.empresa_id;
            
            // 1. Buscar Users (Incluindo photoURL)
            const qUsers = query(collection(db, 'users'), where("empresa_id", "==", empresaId));
            const usersSnap = await getDocs(qUsers);
            const users = usersSnap.docs.map(d => ({ 
                id: d.id, 
                name: d.data().name, 
                email: d.data().email,
                photoURL: d.data().photoURL || null // <--- PEGA A FOTO
            }));
            setUsersList(users.sort((a, b) => a.name.localeCompare(b.name)));

            // 2. Buscar Absences
            const absRef = collection(db, 'Empresas', empresaId, 'absenteismo');
            const qAbsences = query(absRef, orderBy("date", "desc"));
            const absSnap = await getDocs(qAbsences);
            setAbsences(absSnap.docs.map(d => ({ ...d.data(), id: d.id })));
        } catch (error) {
            console.error("Erro ao carregar dados", error);
        }
        setLoading(false);
    }, [userData]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowUserSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- HANDLERS ---
    const filteredUsers = usersList.filter(u => 
        u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || 
        u.email.toLowerCase().includes(userSearchTerm.toLowerCase())
    );

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setUserSearchTerm(user.name);
        setShowUserSuggestions(false);
    };

    const openModal = (abs = null) => {
        if (abs) {
            setEditingId(abs.id);
            // Ao editar, tentamos pegar a foto atualizada da lista, senão null
            const currentUserData = usersList.find(u => u.id === abs.userId);
            setSelectedUser({ 
                id: abs.userId, 
                name: abs.userName, 
                email: abs.userEmail,
                photoURL: currentUserData ? currentUserData.photoURL : null
            });
            setUserSearchTerm(abs.userName);
            setType(abs.type);
            setDate(abs.dateString);
            
            if (abs.hoursDisplay !== undefined) {
                setHours(abs.hoursDisplay);
                setMinutes(abs.minutesDisplay);
            } else {
                const h = Math.floor(abs.hours);
                const m = Math.round((abs.hours - h) * 60);
                setHours(h);
                setMinutes(m);
            }
            
            setReason(abs.reason || '');
            setObservation(abs.observation || '');
            setExistingFileUrl(abs.fileUrl || null);
        } else {
            setEditingId(null);
            setSelectedUser(null);
            setUserSearchTerm('');
            setType('atestado');
            setDate('');
            setHours(0);
            setMinutes(0);
            setReason('');
            setObservation('');
            setExistingFileUrl(null);
        }
        setFile(null);
        if(fileInputRef.current) fileInputRef.current.value = '';
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedUser || !date) return alert("Preencha colaborador e data.");
        if (hours === 0 && minutes === 0) return alert("Informe a duração.");

        setIsUploading(true);
        try {
            const totalHours = parseFloat(hours) + (parseFloat(minutes) / 60);
            let fileUrl = existingFileUrl;

            if (file) {
                const storage = getStorage(getApp());
                const fileRef = ref(storage, `Empresas/${userData.empresa_id}/${STORAGE_PATH}/${Date.now()}_${file.name}`);
                const snapshot = await uploadBytes(fileRef, file);
                fileUrl = await getDownloadURL(snapshot.ref);
            }

            const docData = {
                userId: selectedUser.id,
                userName: selectedUser.name,
                userEmail: selectedUser.email,
                type,
                date: new Date(date),
                dateString: date,
                hours: totalHours,
                hoursDisplay: hours,
                minutesDisplay: minutes,
                reason,
                observation,
                fileUrl: fileUrl || null,
                empresa_id: userData.empresa_id,
                updatedAt: serverTimestamp()
            };

            if (editingId) {
                await updateDoc(doc(db, 'Empresas', userData.empresa_id, 'absenteismo', editingId), docData);
            } else {
                docData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'Empresas', userData.empresa_id, 'absenteismo'), docData);
            }

            closeModal();
            fetchData();
        } catch (error) {
            alert("Erro: " + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm("Remover este lançamento?")) {
            try {
                await deleteDoc(doc(db, 'Empresas', userData.empresa_id, 'absenteismo', id));
                setAbsences(prev => prev.filter(a => a.id !== id));
            } catch (error) { alert("Erro: " + error.message); }
        }
    };

    // Função de upload Excel e Download Template mantidas...
    const handleExcelUpload = (e) => {
         // ... (Mesmo código anterior) ...
         const file = e.target.files[0];
         if (!file) return;
         setIsUploading(true);
         const reader = new FileReader();
         reader.onload = async (evt) => {
             try {
                 const bstr = evt.target.result;
                 const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
                 const ws = wb.Sheets[wb.SheetNames[0]];
                 const data = XLSX.utils.sheet_to_json(ws);
                 if (data.length === 0) throw new Error("Vazio");
                 const promises = data.map(async (row) => {
                     const email = row['Email'] || row['email'];
                     if (!email) return null;
                     const user = usersList.find(u => u.email?.trim().toLowerCase() === email.trim().toLowerCase());
                     if (!user) return null;
                     let jsDate = row['Data'] || row['data'];
                     if (!(jsDate instanceof Date)) jsDate = new Date(jsDate);
                     const h = parseFloat(row['Horas'] || row['horas']) || 0;
                     return addDoc(collection(db, 'Empresas', userData.empresa_id, 'absenteismo'), {
                         userId: user.id, userName: user.name, userEmail: user.email,
                         type: mapExcelTypeToSystem(row['Tipo'] || row['tipo']),
                         date: jsDate, dateString: jsDate.toISOString().split('T')[0],
                         hours: h, hoursDisplay: Math.floor(h), minutesDisplay: Math.round((h - Math.floor(h)) * 60),
                         reason: row['Motivo'] || row['motivo'] || '',
                         observation: row['Observacao'] || row['observacao'] || '',
                         empresa_id: userData.empresa_id, createdAt: serverTimestamp()
                     });
                 });
                 await Promise.all(promises);
                 alert("Importação concluída!");
                 fetchData();
             } catch (error) { alert("Erro na importação: " + error.message); } 
             finally { setIsUploading(false); e.target.value = ''; }
         };
         reader.readAsBinaryString(file);
    };

    const mapExcelTypeToSystem = (rawType) => {
        const t = String(rawType || '').toLowerCase();
        if (t.includes('atestado')) return 'atestado';
        if (t.includes('atraso')) return 'atraso';
        if (t.includes('saída') || t.includes('saida')) return 'saida_antecipada';
        if (t.includes('licença') || t.includes('licenca')) return 'licenca';
        if (t.includes('suspens')) return 'suspensao';
        return 'falta_injustificada';
    };

    const handleDownloadTemplate = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [["Email", "Data", "Tipo", "Horas", "Motivo", "Observacao"], ["joao@empresa.com", "2024-01-15", "Atestado", 8, "Gripe", "Detalhes..."]];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, "Modelo");
        XLSX.writeFile(wb, "Modelo_Absenteismo.xlsx");
    };

    if (loading) return <div className="loading-container"><div className="spinner"></div><p>Carregando...</p></div>;

    // Paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = absences.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(absences.length / itemsPerPage);

    return (
        <div className="abs-container">
            {/* Header */}
            <div className="abs-header">
                <div className="abs-header-info">
                    <div className="abs-icon-box">
                        <FaCalendarTimes />
                    </div>
                    <div className="abs-title">
                        <h1>Gestão de Absenteísmo</h1>
                        <p>Controle de faltas, atrasos e atestados.</p>
                    </div>
                </div>
                <div className="abs-header-actions">
                    <button onClick={handleDownloadTemplate} className="abs-btn secondary" title="Baixar Modelo">
                        <FaDownload /> Modelo
                    </button>
                    <label className="abs-btn secondary" style={{cursor: isUploading ? 'wait' : 'pointer'}}>
                        <FaUpload /> {isUploading ? '...' : 'Importar'}
                        <input type="file" accept=".xlsx" style={{display:'none'}} onChange={handleExcelUpload} disabled={isUploading}/>
                    </label>
                    <button onClick={() => openModal()} className="abs-btn primary">
                        <FaPlus /> Novo Lançamento
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="abs-table-wrapper">
                <table className="abs-table">
                    <thead>
                        <tr>
                            <th>Colaborador</th>
                            <th>Tipo / Motivo</th>
                            <th>Observação</th>
                            <th>Data</th>
                            <th>Duração</th>
                            <th>Anexo</th>
                            <th style={{textAlign: 'right'}}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map(abs => {
                            const typeObj = TYPES.find(t => t.value === abs.type) || { label: abs.type, color: '#777' };
                            // Encontrar o usuário atual na lista para pegar a foto mais recente
                            const currentUser = usersList.find(u => u.id === abs.userId);
                            const photoURL = currentUser ? currentUser.photoURL : null;

                            return (
                                <tr key={abs.id}>
                                    <td>
                                        <div className="user-info-cell">
                                            <div className="user-avatar">
                                                {photoURL ? (
                                                    <img src={photoURL} alt={abs.userName} />
                                                ) : (
                                                    // Fallback para Inicial se não tiver foto
                                                    abs.userName.charAt(0)
                                                )}
                                            </div>
                                            <div className="user-details">
                                                <div className="user-name">{abs.userName}</div>
                                                <div className="user-email">{abs.userEmail}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="type-badge" style={{background: `${typeObj.color}15`, color: typeObj.color}}>
                                            {typeObj.label}
                                        </span>
                                        {abs.reason && <div style={{fontSize: '0.85rem', marginTop: '6px', color:'#555'}}>{abs.reason}</div>}
                                    </td>
                                    <td style={{maxWidth: '220px'}}>
                                        <div style={{fontSize: '0.85rem', color: '#666', lineHeight:'1.4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={abs.observation}>
                                            {abs.observation || '-'}
                                        </div>
                                    </td>
                                    <td style={{color: '#444', fontSize: '0.9rem'}}>
                                        {abs.date && abs.date.seconds 
                                            ? new Date(abs.date.seconds * 1000).toLocaleDateString('pt-BR')
                                            : new Date(abs.dateString).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td>
                                        <div className="time-badge">
                                            <IoTimeOutline />
                                            {abs.hoursDisplay !== undefined 
                                                ? `${abs.hoursDisplay}h ${abs.minutesDisplay > 0 ? abs.minutesDisplay + 'm' : ''}`
                                                : `${abs.hours}h`}
                                        </div>
                                    </td>
                                    <td>
                                        {abs.fileUrl ? (
                                            <a href={abs.fileUrl} target="_blank" rel="noreferrer" className="action-btn view" title="Ver Documento">
                                                {abs.fileUrl.includes('.pdf') ? <FaFilePdf /> : <FaFileImage />}
                                            </a>
                                        ) : <span style={{fontSize:'0.8rem', color:'#ccc', marginLeft:'10px'}}>-</span>}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button onClick={() => openModal(abs)} className="action-btn edit" title="Editar"><FaEdit /></button>
                                            <button onClick={() => handleDelete(abs.id)} className="action-btn delete" title="Excluir"><FaTrash /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {currentItems.length === 0 && <tr><td colSpan="7" style={{padding: '40px', textAlign: 'center', color: '#999'}}>Nenhum registro encontrado.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="abs-pagination">
                <div style={{display:'flex', alignItems:'center'}}>
                    <span style={{fontSize:'14px', color:'#666', marginRight:'10px'}}>Itens por página:</span>
                    <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="abs-select" style={{width: 'auto', padding: '6px'}}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                </div>
                <div className="pagination-controls">
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="page-btn">&lt;</button>
                    <span style={{padding: '6px 12px', fontSize:'14px', color:'#555'}}>Página {currentPage} de {totalPages || 1}</span>
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="page-btn">&gt;</button>
                </div>
            </div>

            {/* MODAL */}
            {showModal && (
                <div className="abs-modal-overlay" onClick={closeModal}>
                    <div className="abs-modal" onClick={e => e.stopPropagation()}>
                        <div className="abs-modal-header">
                            <h2>{editingId ? 'Editar Lançamento' : 'Novo Lançamento'}</h2>
                            <button onClick={closeModal} className="close-modal-btn">&times;</button>
                        </div>
                        <div className="abs-modal-body">
                            <form onSubmit={handleSave} className="abs-form-grid">
                                
                                <div className="abs-form-group full-width search-wrapper" ref={searchRef}>
                                    <label>Colaborador *</label>
                                    <div style={{position:'relative'}}>
                                        <FaSearch className="search-icon" />
                                        <input 
                                            type="text" 
                                            className="abs-input search-input"
                                            placeholder="Buscar nome..."
                                            value={userSearchTerm}
                                            onChange={(e) => { setUserSearchTerm(e.target.value); setShowUserSuggestions(true); }}
                                            onFocus={() => setShowUserSuggestions(true)}
                                            disabled={editingId && selectedUser} 
                                            required
                                        />
                                    </div>
                                    {showUserSuggestions && userSearchTerm && !selectedUser && (
                                        <div className="suggestions-list">
                                            {filteredUsers.length > 0 ? filteredUsers.map(u => (
                                                <div key={u.id} onClick={() => handleSelectUser(u)} className="suggestion-item">
                                                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                                        <div className="user-avatar" style={{width:'30px', height:'30px', fontSize:'12px'}}>
                                                            {u.photoURL ? <img src={u.photoURL} alt={u.name} /> : u.name.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <strong>{u.name}</strong><br/><small style={{color:'#888'}}>{u.email}</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            )) : <div style={{padding:'10px', color:'#999'}}>Nenhum usuário encontrado.</div>}
                                        </div>
                                    )}
                                </div>

                                <div className="abs-form-group">
                                    <label>Tipo *</label>
                                    <select value={type} onChange={e => setType(e.target.value)} className="abs-select">
                                        {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>

                                <div className="abs-form-group">
                                    <label>Data *</label>
                                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="abs-input" required />
                                </div>

                                <div className="abs-form-group full-width">
                                    <label>Duração *</label>
                                    <div style={{display: 'flex', gap: '15px'}}>
                                        <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            <input type="number" min="0" max="24" value={hours} onChange={e => setHours(e.target.value)} className="abs-input" />
                                            <span style={{fontSize: '13px', color: '#666'}}>Horas</span>
                                        </div>
                                        <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            <input type="number" min="0" max="59" value={minutes} onChange={e => setMinutes(e.target.value)} className="abs-input" />
                                            <span style={{fontSize: '13px', color: '#666'}}>Min.</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="abs-form-group full-width">
                                    <label>Motivo (Resumo)</label>
                                    <input type="text" value={reason} onChange={e => setReason(e.target.value)} className="abs-input" placeholder="Ex: Consulta Médica" />
                                </div>

                                <div className="abs-form-group full-width">
                                    <label>Observação (Detalhes)</label>
                                    <textarea rows="3" value={observation} onChange={e => setObservation(e.target.value)} className="abs-textarea" placeholder="Detalhes adicionais..." />
                                </div>

                                <div className="abs-form-group full-width">
                                    <label>Anexo (Opcional)</label>
                                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                        <label className="file-upload-label">
                                            <FaPaperclip /> 
                                            <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                                {file ? file.name : 'Escolher arquivo (PDF/Imagem)...'}
                                            </span>
                                            <input type="file" ref={fileInputRef} onChange={e => setFile(e.target.files[0])} style={{display:'none'}} accept="image/*,.pdf" />
                                        </label>
                                        {existingFileUrl && !file && (
                                            <a href={existingFileUrl} target="_blank" rel="noreferrer" title="Ver atual" style={{fontSize: '20px', color: 'var(--primary-color)'}}>
                                                <FaEye />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="abs-modal-footer full-width">
                                    <button type="button" onClick={closeModal} className="abs-btn secondary">Cancelar</button>
                                    <button type="submit" className="abs-btn primary" disabled={isUploading}>
                                        {isUploading ? 'Salvando...' : 'Salvar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AbsenteismoManager;